import React, { useState, useEffect } from "react";
import AdminLayout from "@/components/admin/AdminLayout";
import { useMenuCategories, useMenuItems } from "@/hooks/useMenu";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Plus, Edit2, Trash2 } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "@/lib/supabase";
import { useQueryClient } from "@tanstack/react-query";

interface CategoryFormData {
  name: string;
  description: string;
}

interface MenuItemFormData {
  name: string;
  description: string;
  price: string;
  category_id: string;
  tags?: string;
}

const AdminMenu: React.FC = () => {
  const { data: categories = [] } = useMenuCategories();
  const { data: menuItems = [] } = useMenuItems();
  const [isAddingCategory, setIsAddingCategory] = useState(false);
  const [isAddingMenuItem, setIsAddingMenuItem] = useState(false);
  const [editingCategory, setEditingCategory] = useState<number | null>(null);
  const [editingMenuItem, setEditingMenuItem] = useState<number | null>(null);
  const [categoryForm, setCategoryForm] = useState<CategoryFormData>({ name: "", description: "" });
  const [menuItemForm, setMenuItemForm] = useState<MenuItemFormData>({
    name: "",
    description: "",
    price: "",
    category_id: categories[0]?.id || "",
    tags: "",
  });
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Keep menuItemForm.category_id in sync with categories
  useEffect(() => {
    setMenuItemForm((prev) => ({ ...prev, category_id: categories[0]?.id || "" }));
  }, [categories]);

  const handleAddCategory = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const { error } = await supabase
        .from("menu_categories")
        .insert([categoryForm]);

      if (error) throw error;

      toast({ title: "Success", description: "Category added successfully" });
      setIsAddingCategory(false);
      setCategoryForm({ name: "", description: "" });
      queryClient.invalidateQueries({ queryKey: ["menu-categories"] });
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to add category",
      });
    }
  };

  const handleUpdateCategory = async (id: number) => {
    try {
      const { error } = await supabase
        .from("menu_categories")
        .update(categoryForm)
        .eq("id", id);

      if (error) throw error;

      toast({ title: "Success", description: "Category updated successfully" });
      setEditingCategory(null);
      setCategoryForm({ name: "", description: "" });
      queryClient.invalidateQueries({ queryKey: ["menu-categories"] });
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to update category",
      });
    }
  };

  const handleDeleteCategory = async (id: number) => {
    if (!confirm("Are you sure? This will also delete all menu items in this category.")) return;

    try {
      const { error } = await supabase
        .from("menu_categories")
        .delete()
        .eq("id", id);

      if (error) throw error;

      toast({ title: "Success", description: "Category deleted successfully" });
      queryClient.invalidateQueries({ queryKey: ["menu-categories"] });
      queryClient.invalidateQueries({ queryKey: ["menu-items"] });
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to delete category",
      });
    }
  };

  const handleAddMenuItem = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const { error } = await supabase
        .from("menu_items")
        .insert([{
          ...menuItemForm,
          price: parseFloat(menuItemForm.price),
          tags: menuItemForm.tags ? menuItemForm.tags.split(",").map(t => t.trim()).filter(Boolean) : [],
        }]);

      if (error) throw error;

      toast({ title: "Success", description: "Menu item added successfully" });
      setIsAddingMenuItem(false);
      setMenuItemForm({
        name: "",
        description: "",
        price: "",
        category_id: categories[0]?.id || "",
        tags: "",
      });
      queryClient.invalidateQueries({ queryKey: ["menu-items"] });
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to add menu item",
      });
    }
  };

  const handleUpdateMenuItem = async (id: number) => {
    try {
      const { error } = await supabase
        .from("menu_items")
        .update({
          ...menuItemForm,
          price: parseFloat(menuItemForm.price),
          tags: menuItemForm.tags ? menuItemForm.tags.split(",").map(t => t.trim()).filter(Boolean) : [],
        })
        .eq("id", id);

      if (error) throw error;

      toast({ title: "Success", description: "Menu item updated successfully" });
      setEditingMenuItem(null);
      setMenuItemForm({
        name: "",
        description: "",
        price: "",
        category_id: categories[0]?.id || "",
        tags: "",
      });
      queryClient.invalidateQueries({ queryKey: ["menu-items"] });
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to update menu item",
      });
    }
  };

  const handleDeleteMenuItem = async (id: number) => {
    if (!confirm("Are you sure you want to delete this menu item?")) return;

    try {
      const { error } = await supabase
        .from("menu_items")
        .delete()
        .eq("id", id);

      if (error) throw error;

      toast({ title: "Success", description: "Menu item deleted successfully" });
      queryClient.invalidateQueries({ queryKey: ["menu-items"] });
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to delete menu item",
      });
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-8">
        <div>
          <h1 className="text-3xl font-display font-bold text-lounge-gold">Menu Management</h1>
          <p className="text-lounge-light/60 mt-2">Manage your menu categories and items</p>
        </div>

        {/* Categories Section */}
        <div className="bg-lounge-charcoal p-6 rounded-lg border border-lounge-gold/20">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-display font-semibold text-lounge-gold">Categories</h2>
            <Button
              onClick={() => setIsAddingCategory(true)}
              className="bg-lounge-gold hover:bg-lounge-gold/90"
            >
              <Plus className="w-4 h-4 mr-2" /> Add Category
            </Button>
          </div>

          {isAddingCategory && (
            <form onSubmit={handleAddCategory} className="mb-6 space-y-4 bg-lounge-dark p-4 rounded-lg">
              <Input
                placeholder="Category Name"
                value={categoryForm.name}
                onChange={(e) => setCategoryForm({ ...categoryForm, name: e.target.value })}
                className="bg-lounge-charcoal border-lounge-gold/20"
                required
              />
              <Textarea
                placeholder="Category Description"
                value={categoryForm.description}
                onChange={(e) => setCategoryForm({ ...categoryForm, description: e.target.value })}
                className="bg-lounge-charcoal border-lounge-gold/20"
                required
              />
              <div className="flex gap-2">
                <Button type="submit" className="bg-lounge-gold hover:bg-lounge-gold/90">
                  Save
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setIsAddingCategory(false);
                    setCategoryForm({ name: "", description: "" });
                  }}
                >
                  Cancel
                </Button>
              </div>
            </form>
          )}

          <div className="space-y-4">
            {categories.map((category) => (
              <div
                key={category.id}
                className="flex items-center justify-between p-4 bg-lounge-dark rounded-lg"
              >
                {editingCategory === category.id ? (
                  <div className="flex-1 space-y-4">
                    <Input
                      value={categoryForm.name}
                      onChange={(e) => setCategoryForm({ ...categoryForm, name: e.target.value })}
                      className="bg-lounge-charcoal border-lounge-gold/20"
                    />
                    <Textarea
                      value={categoryForm.description}
                      onChange={(e) => setCategoryForm({ ...categoryForm, description: e.target.value })}
                      className="bg-lounge-charcoal border-lounge-gold/20"
                    />
                    <div className="flex gap-2">
                      <Button
                        onClick={() => handleUpdateCategory(category.id)}
                        className="bg-lounge-gold hover:bg-lounge-gold/90"
                      >
                        Save
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => {
                          setEditingCategory(null);
                          setCategoryForm({ name: "", description: "" });
                        }}
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div>
                      <h3 className="font-medium text-lounge-light">{category.name}</h3>
                      <p className="text-sm text-lounge-light/60">{category.description}</p>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => {
                          setEditingCategory(category.id);
                          setCategoryForm({
                            name: category.name,
                            description: category.description,
                          });
                        }}
                      >
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="icon"
                        className="text-red-500 hover:text-red-600"
                        onClick={() => handleDeleteCategory(category.id)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Menu Items Section */}
        <div className="bg-lounge-charcoal p-6 rounded-lg border border-lounge-gold/20">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-display font-semibold text-lounge-gold">Menu Items</h2>
            <Button
              onClick={() => setIsAddingMenuItem(true)}
              className="bg-lounge-gold hover:bg-lounge-gold/90"
              disabled={categories.length === 0}
            >
              <Plus className="w-4 h-4 mr-2" /> Add Menu Item
            </Button>
          </div>

          {isAddingMenuItem && (
            <form onSubmit={handleAddMenuItem} className="mb-6 space-y-4 bg-lounge-dark p-4 rounded-lg">
              <select
                value={menuItemForm.category_id}
                onChange={(e) => setMenuItemForm({ ...menuItemForm, category_id: e.target.value })}
                className="w-full bg-lounge-charcoal border border-lounge-gold/20 rounded-md p-2 text-lounge-light"
                required
              >
                {categories.map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </select>
              <Input
                placeholder="Item Name"
                value={menuItemForm.name}
                onChange={(e) => setMenuItemForm({ ...menuItemForm, name: e.target.value })}
                className="bg-lounge-charcoal border-lounge-gold/20"
                required
              />
              <Input
                placeholder="Tags (comma separated, e.g. Cocktail, Beer, Mocktail)"
                value={menuItemForm.tags || ""}
                onChange={(e) => setMenuItemForm({ ...menuItemForm, tags: e.target.value })}
                className="bg-lounge-charcoal border-lounge-gold/20"
                type="text"
              />
              <Textarea
                placeholder="Item Description"
                value={menuItemForm.description}
                onChange={(e) => setMenuItemForm({ ...menuItemForm, description: e.target.value })}
                className="bg-lounge-charcoal border-lounge-gold/20"
                required
              />
              <Input
                type="number"
                step="0.01"
                placeholder="Price"
                value={menuItemForm.price}
                onChange={(e) => setMenuItemForm({ ...menuItemForm, price: e.target.value })}
                className="bg-lounge-charcoal border-lounge-gold/20"
                required
              />
              <div className="flex gap-2">
                <Button type="submit" className="bg-lounge-gold hover:bg-lounge-gold/90">
                  Save
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setIsAddingMenuItem(false);
                    setMenuItemForm({
                      name: "",
                      description: "",
                      price: "",
                      category_id: categories[0]?.id || "",
                      tags: "",
                    });
                  }}
                >
                  Cancel
                </Button>
              </div>
            </form>
          )}

          <div className="space-y-4">
            {categories.map((category) => {
              const categoryItems = menuItems.filter((item) => item.category_id === category.id);
              return (
                <div key={category.id} className="space-y-2">
                  <h3 className="font-medium text-lounge-gold">{category.name}</h3>
                  <div className="space-y-2">
                    {categoryItems.map((item) => (
                      <div
                        key={item.id}
                        className="flex items-center justify-between p-4 bg-lounge-dark rounded-lg"
                      >
                        {editingMenuItem === item.id ? (
                          <div className="flex-1 space-y-4">
                            <Input
                              value={menuItemForm.name}
                              onChange={(e) => setMenuItemForm({ ...menuItemForm, name: e.target.value })}
                              className="bg-lounge-charcoal border-lounge-gold/20"
                            />
                            <Input
                              placeholder="Tags (comma separated, e.g. Cocktail, Beer, Mocktail)"
                              value={menuItemForm.tags || ""}
                              onChange={(e) => setMenuItemForm({ ...menuItemForm, tags: e.target.value })}
                              className="bg-lounge-charcoal border-lounge-gold/20"
                              type="text"
                            />
                            <Textarea
                              value={menuItemForm.description}
                              onChange={(e) => setMenuItemForm({ ...menuItemForm, description: e.target.value })}
                              className="bg-lounge-charcoal border-lounge-gold/20"
                            />
                            <Input
                              type="number"
                              step="0.01"
                              value={menuItemForm.price}
                              onChange={(e) => setMenuItemForm({ ...menuItemForm, price: e.target.value })}
                              className="bg-lounge-charcoal border-lounge-gold/20"
                            />
                            <div className="flex gap-2">
                              <Button
                                onClick={() => handleUpdateMenuItem(item.id)}
                                className="bg-lounge-gold hover:bg-lounge-gold/90"
                              >
                                Save
                              </Button>
                              <Button
                                variant="outline"
                                onClick={() => {
                                  setEditingMenuItem(null);
                                  setMenuItemForm({
                                    name: "",
                                    description: "",
                                    price: "",
                                    category_id: categories[0]?.id || "",
                                    tags: "",
                                  });
                                }}
                              >
                                Cancel
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <>
                            <div>
                              <h4 className="font-medium text-lounge-light">{item.name}</h4>
                              <p className="text-sm text-lounge-light/60">{item.description}</p>
                              <p className="text-sm text-lounge-gold mt-1">${item.price.toFixed(2)}</p>
                            </div>
                            <div className="flex gap-2">
                              <Button
                                variant="outline"
                                size="icon"
                                onClick={() => {
                                  setEditingMenuItem(item.id);
                                  setMenuItemForm({
                                    name: item.name,
                                    description: item.description,
                                    price: item.price.toString(),
                                    category_id: String(item.category_id),
                                    tags: Array.isArray(item.tags) ? item.tags.join(", ") : "",
                                  });
                                }}
                              >
                                <Edit2 className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="outline"
                                size="icon"
                                className="text-red-500 hover:text-red-600"
                                onClick={() => handleDeleteMenuItem(item.id)}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminMenu; 